import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';
import { fmtDate, todayStr } from '../lib/format.js';
import { ProfileSection } from './ProfileSection.jsx';

// ─── Employee Profile (Round 8) ──────────────────────────────────────────────
// Click-through from Team. Sections: Contact / Emergency / Locations + Role /
// Pay & PTO / Performance card. Editability is fully driven by the canEdit
// object the server sends back, so the same UI handles self / manager / admin
// without role math leaking into the component.
// Employment timeline card — manager + owner can see + record hire /
// separation / leave events. Backfilled from users.hire_date so the
// existing roster has at least one row each. CJ feedback 2026-05-08.
export function EmploymentTimelineCard({ currentUser, targetUserId, targetName }) {
  const headers = { 'x-user-id': String(currentUser.id) };
  const isManager = /^(owner|admin|administrator|manager)$/i.test(currentUser?.role || '');
  // CJ feedback 2026-05-08: hire / rehire / leave_end edit start dates,
  // which is the historical record. Restricted to owners. Managers can
  // record separation + leave_start (the things they live with day to
  // day). Server enforces the same rule — UI just hides the options so
  // managers don't get a 403 surprise.
  const isOwnerLevel = /^(owner|admin|administrator)$/i.test(currentUser?.role || '');
  const eventTypeOptions = isOwnerLevel
    ? [
        { value: 'hire', label: 'Hire' },
        { value: 'rehire', label: 'Rehire' },
        { value: 'separation', label: 'Separation' },
        { value: 'leave_start', label: 'Leave start' },
        { value: 'leave_end', label: 'Leave end' },
      ]
    : [
        { value: 'separation', label: 'Separation' },
        { value: 'leave_start', label: 'Leave start' },
      ];
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({
    event_type: 'separation', event_date: todayStr(),
    separation_kind: 'resigned', reason_notes: '',
  });
  const [savingMsg, setSavingMsg] = useState('');

  async function load() {
    setLoading(true);
    try {
      const r = await api('GET', `/api/admin/users/${targetUserId}/employment-events`, null, headers);
      setEvents(Array.isArray(r?.events) ? r.events : []);
    } catch (_) { setEvents([]); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, [targetUserId]);

  async function save() {
    if (!draft.event_date || !/^\d{4}-\d{2}-\d{2}$/.test(draft.event_date)) {
      setSavingMsg('Pick a date.'); return;
    }
    setSavingMsg('Saving…');
    try {
      const payload = {
        event_type: draft.event_type,
        event_date: draft.event_date,
        reason_notes: draft.reason_notes || null,
      };
      if (draft.event_type === 'separation') payload.separation_kind = draft.separation_kind;
      await api('POST', `/api/admin/users/${targetUserId}/employment-events`, payload, headers);
      setSavingMsg('');
      setAdding(false);
      setDraft({ event_type: 'separation', event_date: todayStr(),
                 separation_kind: 'resigned', reason_notes: '' });
      await load();
    } catch (e) { setSavingMsg(`Failed: ${e.message || e}`); }
  }

  function fmtDate(d) {
    if (!d) return '';
    try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
    catch { return ''; }
  }
  function eventColor(type) {
    if (type === 'hire' || type === 'rehire') return { bg: '#e6f4ea', fg: '#1e7d3a', bd: '#c8e2c8' };
    if (type === 'separation') return { bg: '#fef2f2', fg: '#a13030', bd: '#fecaca' };
    return { bg: '#fef9ee', fg: '#7a5b1a', bd: '#f0d9a3' };
  }
  function kindLabel(k) {
    const map = {
      resigned: 'Resigned',
      terminated_for_cause: 'Terminated — for cause',
      terminated_no_cause: 'Terminated — no cause',
      seasonal_end: 'Seasonal end',
      job_abandonment: 'Job abandonment',
      mutual: 'Mutual',
      medical: 'Medical',
      other: 'Other',
    };
    return map[k] || k;
  }

  return (
    <ProfileSection title="Employment timeline">
      <div style={{ fontSize: 13, color: '#666', marginBottom: 10 }}>
        Hire / separation / leave events. Used for tenure tracking,
        seasonal-staff records, and unemployment-claim defense.
      </div>
      {loading ? (
        <div style={{ fontSize: 12, color: '#888' }}>Loading…</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {events.length === 0 && (
            <div style={{ fontSize: 12, color: '#888' }}>No events recorded yet.</div>
          )}
          {events.map(e => {
            const c = eventColor(e.event_type);
            return (
              <div key={e.id} style={{ border: `1px solid ${c.bd}`, background: c.bg,
                                        borderRadius: 6, padding: 8, fontSize: 13 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8 }}>
                  <span>
                    <strong style={{ color: c.fg, textTransform: 'uppercase', fontSize: 11, letterSpacing: '0.06em' }}>
                      {e.event_type.replace(/_/g, ' ')}
                    </strong>
                    {e.separation_kind && <span style={{ marginLeft: 8 }}>· {kindLabel(e.separation_kind)}</span>}
                  </span>
                  <span style={{ color: '#888', fontSize: 12 }}>{fmtDate(e.event_date)}</span>
                </div>
                {e.reason_notes && <div style={{ marginTop: 4, color: '#444' }}>{e.reason_notes}</div>}
                {e.recorded_by_name && (
                  <div style={{ marginTop: 4, fontSize: 10, color: '#9e9e9e' }}>
                    Recorded by {e.recorded_by_name}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {isManager && !adding && (
        <button onClick={() => setAdding(true)}
          style={{ marginTop: 10, padding: '6px 14px', fontSize: 13, fontWeight: 600,
                    border: '1px solid var(--gold)', background: '#fff', color: '#212121',
                    borderRadius: 6, cursor: 'pointer' }}>
          + Record event
        </button>
      )}
      {isManager && adding && (
        <div style={{ marginTop: 12, padding: 12, background: '#fafafa', border: '1px solid #e0e0e0', borderRadius: 8 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 8, marginBottom: 10 }}>
            <label style={{ fontSize: 12, color: '#555', display: 'flex', flexDirection: 'column', gap: 4 }}>
              Event type
              <select value={draft.event_type}
                onChange={e => setDraft(d => ({ ...d, event_type: e.target.value }))}
                style={{ border: '1px solid #d0d4d9', borderRadius: 6, padding: 6, fontSize: 13 }}>
                {eventTypeOptions.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </label>
            <label style={{ fontSize: 12, color: '#555', display: 'flex', flexDirection: 'column', gap: 4 }}>
              Date
              <input type="date" value={draft.event_date}
                onChange={e => setDraft(d => ({ ...d, event_date: e.target.value }))}
                style={{ border: '1px solid #d0d4d9', borderRadius: 6, padding: 6, fontSize: 13 }} />
            </label>
            {draft.event_type === 'separation' && (
              <label style={{ fontSize: 12, color: '#555', display: 'flex', flexDirection: 'column', gap: 4 }}>
                Kind
                <select value={draft.separation_kind}
                  onChange={e => setDraft(d => ({ ...d, separation_kind: e.target.value }))}
                  style={{ border: '1px solid #d0d4d9', borderRadius: 6, padding: 6, fontSize: 13 }}>
                  <option value="resigned">Resigned</option>
                  <option value="terminated_for_cause">Terminated — for cause</option>
                  <option value="terminated_no_cause">Terminated — no cause</option>
                  <option value="seasonal_end">Seasonal end</option>
                  <option value="job_abandonment">Job abandonment</option>
                  <option value="mutual">Mutual</option>
                  <option value="medical">Medical</option>
                  <option value="other">Other</option>
                </select>
              </label>
            )}
          </div>
          <label style={{ fontSize: 12, color: '#555', display: 'block', marginBottom: 4 }}>Notes</label>
          <textarea value={draft.reason_notes}
            onChange={e => setDraft(d => ({ ...d, reason_notes: e.target.value }))}
            rows={3}
            placeholder="What happened, what was discussed, dates of last shifts, etc."
            style={{ width: '100%', border: '1px solid #d0d4d9', borderRadius: 6, padding: 8, fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' }} />
          <div style={{ marginTop: 10, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            {savingMsg && <span style={{ fontSize: 12, color: savingMsg.startsWith('Failed') ? '#b00020' : '#666', alignSelf: 'center' }}>{savingMsg}</span>}
            <button onClick={() => { setAdding(false); setSavingMsg(''); }}
              style={{ fontSize: 13, padding: '6px 14px', border: '1px solid #d0d4d9', background: '#fff', borderRadius: 6, cursor: 'pointer' }}>Cancel</button>
            <button onClick={save}
              style={{ fontSize: 13, padding: '6px 16px', border: 0, background: 'var(--gold)', color: '#fff', fontWeight: 600, borderRadius: 6, cursor: 'pointer' }}>
              Save event
            </button>
          </div>
        </div>
      )}
    </ProfileSection>
  );
}
